/**
 * Every path in the app, in one place.
 *
 * Inline path literals scattered through components are how a rename turns
 * into a silent 404 six files away — nothing checks a string passed to `<Link
 * to>`. Route-building functions go here too as the tree grows
 * (`project(id)`, `site(projectId, siteId)`).
 */
export const ROUTES = {
  root: '/',

  /**
   * Fixed by the API, not chosen here. `OAuthController` sends a refused
   * sign-in to `{PORTAL_URL}/sign-in?error=CODE` (P0-PLAT-08), so renaming this
   * route silently turns every OAuth failure into a 404.
   */
  signIn: '/sign-in',
  signUp: '/sign-up',
  forgotPassword: '/forgot-password',
  resetPassword: '/reset-password',
  verifyEmail: '/verify-email',

  /** The full project listing — PRD §PROJ-7's "See All Projects". The globe
   *  that lands on it is P1-WEB-01. */
  projects: '/projects',

  /** The Tools canvas (P1-WEB-28, ADR-0016): imagery and analysis on a drawn
   *  area, no project needed until the planner wants to keep one. */
  tools: '/tools',

  /** Devices and sessions (PRD §AUTH-8). The rest of the account menu — the
   *  profile — waits on P0-PLAT-20. */
  accountSessions: '/account/sessions',
} as const;

/**
 * The workspace for one project (P1-WEB-04, PRD §WS-1..9).
 *
 * A function rather than a constant, because it takes an id — and it lives here
 * for the same reason the constants do. `ProjectCard` built this path inline
 * until P1-WEB-04, which is exactly the failure this file exists to prevent: a
 * link to a route that did not exist yet, rendering the not-found page with
 * nothing anywhere reporting it.
 */
export function projectPath(projectId: string): string {
  return `/projects/${projectId}`;
}

/** The pattern the router registers, as opposed to a filled-in path. */
export const PROJECT_ROUTE = '/projects/:projectId';

/**
 * A digitise run's web report (P1-MVP-30). Under its project, so the way back
 * is the workspace the run came from; addressed by the run, because one run
 * is one report.
 */
export function reportPath(projectId: string, runId: string): string {
  return `/projects/${projectId}/runs/${runId}/report`;
}

export const REPORT_ROUTE = '/projects/:projectId/runs/:runId/report';

/**
 * The council dashboard for one project (P1-WEB-38): a site's land cover
 * across its capture dates. Under its project, like the report, so the way
 * back is the workspace.
 */
export function dashboardPath(projectId: string): string {
  return `/projects/${projectId}/dashboard`;
}

export const DASHBOARD_ROUTE = '/projects/:projectId/dashboard';

/**
 * PROJ-7's "See All Projects" page (P1-WEB-02).
 *
 * A sibling of the workspace rather than a child: `/projects` lists and
 * `/projects/{id}` opens one, which is the shape every link in the product
 * already assumes.
 */
export const PROJECTS_LIST_ROUTE = '/projects';

export type RoutePath = (typeof ROUTES)[keyof typeof ROUTES];

/** The query parameter carrying where the user was going before they were sent
 *  to sign in. P0-WEB-07's protected loader sets it. */
export const NEXT_PARAM = 'next';

/** The query parameter the OAuth callback returns a failure code in. */
export const OAUTH_ERROR_PARAM = 'error';

/**
 * Where to go after signing in.
 *
 * Anything that is not a path on this origin is rejected in favour of the root.
 * `next` comes from the URL bar, so without this a link to
 * `/sign-in?next=https://evil.example` turns our own sign-in form into a
 * credible redirect to somebody else's — the classic open redirect, and one
 * that is far more convincing coming from a page the user just typed a
 * password into.
 */
export function safeNextPath(next: string | null): string {
  if (next === null || next === '') return ROUTES.root;

  // `//host` and `/\host` are protocol-relative: the browser reads them as
  // another origin even though they start with a slash.
  if (!next.startsWith('/') || next.startsWith('//') || next.startsWith('/\\')) {
    return ROUTES.root;
  }

  // Browsers strip tabs, newlines and other control characters from a URL
  // before resolving it, so `/\tjavascript:…` and `/\n/evil.example` are not
  // the shapes they look like by the time they are followed.
  // eslint-disable-next-line no-control-regex
  if (/[\u0000-\u001F\u007F]/.test(next)) return ROUTES.root;

  return next;
}

/**
 * What a route can tell the shell about itself, through React Router's
 * `handle`. `fullscreen` drops the header and navigation rail: the Tools
 * canvas is the whole window, with its own dock and account menu.
 */
export interface RouteHandle {
  readonly fullscreen?: boolean;
}
